import React, { useState, useMemo } from "react";
import CoverageMap from "./CoverageMap";
import { coverage } from "../Coverage";
import { FiMapPin, FiSearch, FiCheck, FiX, FiLayers, FiHelpCircle, FiPhone } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

export default function CoverageSection() {
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegionId, setSelectedRegionId] = useState("all");

  const getRegionLocalizedTitle = (title) => {
    if (language === "en") {
      const map = {
        "Արմավիր": "Armavir",
        "Արագածոտն": "Aragatsotn",
        "Արարատ": "Ararat",
        "Երևան": "Yerevan",
        "Կոտայք": "Kotayk",
      };
      return map[title] || title;
    }
    if (language === "ru") {
      const map = {
        "Արմավիր": "Армавир",
        "Արագածոտն": "Арагацотн",
        "Արարատ": "Арарат",
        "Երևան": "Ереван",
        "Կոտայք": "Котайк",
      };
      return map[title] || title;
    }
    return title;
  };

  // Flattened locations for search
  const allLocations = useMemo(() => {
    return coverage.flatMap((region) =>
      region.locations.map((loc) => ({
        ...loc,
        regionTitle: region.title,
        regionId: region.id,
      }))
    );
  }, []);

  // Filtered locations
  const filteredLocations = useMemo(() => {
    return allLocations.filter((item) => {
      const matchesSearch =
        item.title.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
        item.regionTitle.toLowerCase().includes(searchTerm.toLowerCase().trim());
      const matchesRegion =
        selectedRegionId === "all" || item.regionId === selectedRegionId;
      return matchesSearch && matchesRegion;
    });
  }, [allLocations, searchTerm, selectedRegionId]);

  return (
    <section id="coverage" className="w-full py-20 bg-slate-50/70 border-t border-slate-100">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-brandRed text-xs font-bold uppercase tracking-wider mb-3">
            <FiMapPin size={14} />
            <span>{t("coverage.badge")}</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight uppercase">
            {t("coverage.title")}
          </h2>
          <p className="text-slate-600 text-sm md:text-base mt-3 leading-relaxed">
            {t("coverage.subtitle")}
          </p>
        </div>

        {/* Map & Search Controls Container */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Map Column */}
          <div className="lg:col-span-7 bg-white p-3 rounded-3xl border border-slate-200/80 shadow-premium overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 mb-2 text-xs font-bold text-slate-600 uppercase tracking-wider">
              <span className="flex items-center space-x-1.5">
                <FiLayers className="text-brandRed" />
                <span>{t("coverage.mapTitle")}</span>
              </span>
              <span className="text-slate-400 font-medium">
                {allLocations.length} {t("coverage.settlementsCount")}
              </span>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-100">
              <CoverageMap />
            </div>
            <div className="p-3 mt-1 flex flex-wrap items-center justify-center gap-4 text-xs font-medium text-slate-600">
              <div className="flex items-center space-x-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-brandRed inline-block"></span>
                <span>{t("coverage.markerFiber")}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>
                <span>{t("coverage.markerActive")}</span>
              </div>
            </div>
          </div>

          {/* Directory & Search Column */}
          <div className="lg:col-span-5 bg-white rounded-3xl border border-slate-200/80 shadow-premium p-6 flex flex-col h-[560px]">
            {/* Search Input */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <FiSearch size={16} />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("coverage.searchPlaceholder")}
                className="w-full pl-10 pr-4 py-3 rounded-2xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-brandRed focus:ring-2 focus:ring-red-100 transition-all"
              />
            </div>

            {/* Region Filter Buttons */}
            <div className="flex items-center space-x-2 pb-3 overflow-x-auto text-xs font-bold uppercase tracking-wider flex-shrink-0">
              <button
                onClick={() => setSelectedRegionId("all")}
                className={`px-3 py-1.5 rounded-xl transition-colors whitespace-nowrap cursor-pointer ${
                  selectedRegionId === "all"
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {t("coverage.allRegions")} ({allLocations.length})
              </button>
              {coverage.map((reg) => (
                <button
                  key={reg.id}
                  onClick={() => setSelectedRegionId(reg.id)}
                  className={`px-3 py-1.5 rounded-xl transition-colors whitespace-nowrap cursor-pointer ${
                    selectedRegionId === reg.id
                      ? "bg-brandRed text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {getRegionLocalizedTitle(reg.title)} ({reg.locations.length})
                </button>
              ))}
            </div>

            {/* Filtered Results List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1 mt-2">
              {filteredLocations.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">
                  <FiMapPin className="mx-auto mb-2 text-slate-300" size={28} />
                  <span>{t("coverage.noResults")}</span>
                </div>
              ) : (
                filteredLocations.map((item, idx) => (
                  <div
                    key={`${item.id}-${idx}`}
                    className="p-3 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-slate-200 hover:shadow-sm transition-all flex items-center justify-between"
                  >
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">
                        {item.title}
                      </h4>
                      <span className="text-[11px] text-slate-400 font-medium">
                        {getRegionLocalizedTitle(item.regionTitle)}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                          item.fiber
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {item.fiber ? (
                          <>
                            <FiCheck size={11} className="text-emerald-600" />
                            <span>{t("coverage.fiberAvailable")}</span>
                          </>
                        ) : (
                          <>
                            <FiX size={11} />
                            <span>{t("coverage.fiberNone")}</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Bottom notification */}
            <div className="pt-4 border-t border-slate-100 mt-2 flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center space-x-1.5">
                <FiHelpCircle className="text-brandRed" />
                <span>{t("coverage.cantFind")}</span>
              </span>
              <a
                href="tel:+37460666999"
                className="text-brandRed hover:text-brandRedHover font-bold uppercase text-[11px] flex items-center space-x-1"
              >
                <FiPhone size={12} />
                <span>{t("coverage.checkByCall")}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
